/**
 * Corridas: el message log agrupado por correlation id.
 *
 * El listado de mensajes tiene una fila por mensaje y una sola corrida de F8
 * deja miles, asi que buscar "la corrida de ayer de F8" ahi era hojear paginas.
 * Aca cada corrida es una fila y se abre en el grafo.
 *
 * En los flujos por lote (F3, F8, F12) una corrida es una ejecucion completa; en
 * los que parten por webhook (F1, Multivende) es una entidad —un pedido—, y por
 * eso el filtro de correlation id busca por parte del texto.
 *
 * Mismas reglas que Mensajes: los filtros viven en la URL (se pueden compartir),
 * el refresco es silencioso y en un telefono los filtros se pliegan.
 */
import { useEffect, useId, useMemo, useState } from 'react';
import type { ReactElement } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import type { FlowRunSummary } from '../api/contracts';
import type { PageState, RunFilters } from '../api/types';
import { useMonitorApi } from '../app/api-context';
import { AutoRefreshToggle } from '../components/AutoRefreshToggle';
import { ErrorBanner } from '../components/ErrorBanner';
import { EventName } from '../components/EventName';
import { EmptyState, LoadingBlock } from '../components/Feedback';
import { IconChevronRight, IconFilter } from '../components/icons';
import { Pagination } from '../components/Pagination';
import { SelectFilter } from '../components/SelectFilter';
import { RunStatusBadge } from '../components/StatusBadge';
import { useAutoRefreshPreference } from '../hooks/use-auto-refresh-preference';
import { usePolledResource } from '../hooks/use-polled-resource';
import { REFRESH_INTERVAL_MS } from '../lib/auto-refresh';
import { FLOW_OPTIONS, RUN_STATUS_OPTIONS, flowLabel, systemLabel } from '../lib/catalogs';
import {
  EMPTY_RUN_FILTERS,
  activeRunFilterCount,
  pageStateFromSearchParams,
  runFiltersFromSearchParams,
  runFiltersToSearchParams,
} from '../lib/filters';
import { formatDateTime, formatDuration, shortId } from '../lib/format';
import '../styles/flow-chip.css';

function filtersToggleLabel(active: number): string {
  return active === 0 ? 'Filtros' : `Filtros: ${active} ${active === 1 ? 'activo' : 'activos'}`;
}

export function RunsPage(): ReactElement {
  const api = useMonitorApi();
  const [searchParams, setSearchParams] = useSearchParams();

  const filters = useMemo(() => runFiltersFromSearchParams(searchParams), [searchParams]);
  const page = useMemo(() => pageStateFromSearchParams(searchParams), [searchParams]);
  const queryKey = searchParams.toString();

  // Los campos de texto y fecha se aplican al enviar, no en cada tecla.
  const [draft, setDraft] = useState<RunFilters>(filters);
  useEffect(() => {
    setDraft(filters);
  }, [filters]);

  const [filtersOpen, setFiltersOpen] = useState(false);
  const filtersId = useId();

  const [autoRefresh, setAutoRefresh] = useAutoRefreshPreference('runs');
  const resource = usePolledResource(() => api.listRuns(filters, page), [api, queryKey], {
    intervalMs: REFRESH_INTERVAL_MS.runs,
    enabled: autoRefresh,
  });

  const apply = (next: RunFilters, nextPage: PageState = { ...page, page: 1 }): void => {
    setSearchParams(runFiltersToSearchParams(next, nextPage));
  };

  const rows = resource.data?.items ?? [];
  const activeFilters = activeRunFilterCount(filters);

  return (
    <div className="page">
      <header className="page__header">
        <div>
          <h1>Corridas</h1>
          <p className="page__subtitle">
            Una fila por corrida: todos los mensajes que comparten correlation id, con su estado
            agregado. Ábrela para ver su flujo y sus corridas anteriores.
          </p>
        </div>
        <div className="row">
          <AutoRefreshToggle
            enabled={autoRefresh}
            onChange={setAutoRefresh}
            intervalMs={REFRESH_INTERVAL_MS.runs}
            updatedAt={resource.updatedAt}
            failing={resource.error !== null}
          />
          <button type="button" className="button" onClick={resource.reload}>
            Actualizar
          </button>
        </div>
      </header>

      <section
        className={filtersOpen ? 'panel filters-panel filters-panel--open' : 'panel filters-panel'}
      >
        <button
          type="button"
          className="filters-panel__toggle"
          aria-expanded={filtersOpen}
          aria-controls={filtersId}
          aria-label={filtersToggleLabel(activeFilters)}
          onClick={() => setFiltersOpen((open) => !open)}
        >
          <IconFilter size={16} />
          Filtros
          {activeFilters === 0 ? null : (
            <span className="filters-panel__count">{activeFilters}</span>
          )}
          <IconChevronRight size={16} className="filters-panel__chevron" />
        </button>
        <div id={filtersId} className="panel__body filters-panel__body">
          <form
            className="filters"
            onSubmit={(event) => {
              event.preventDefault();
              apply(draft);
            }}
          >
            <SelectFilter
              label="Flujo"
              value={draft.flow}
              options={FLOW_OPTIONS}
              onChange={(value) => apply({ ...draft, flow: value })}
            />
            <SelectFilter
              label="Estado"
              value={draft.status}
              options={RUN_STATUS_OPTIONS}
              onChange={(value) => apply({ ...draft, status: value })}
            />

            <label className="field field--wide">
              <span className="field__label">Correlation id</span>
              <input
                className="input input--mono"
                type="search"
                value={draft.correlationId}
                placeholder="parte del id, o el número del pedido (PV-…)"
                onChange={(event) => setDraft({ ...draft, correlationId: event.target.value })}
              />
            </label>

            <label className="field field--datetime">
              <span className="field__label">Inicio desde</span>
              <input
                className="input"
                type="datetime-local"
                value={draft.from}
                onChange={(event) => setDraft({ ...draft, from: event.target.value })}
              />
            </label>

            <label className="field field--datetime">
              <span className="field__label">Inicio hasta</span>
              <input
                className="input"
                type="datetime-local"
                value={draft.to}
                onChange={(event) => setDraft({ ...draft, to: event.target.value })}
              />
            </label>

            <div className="row">
              <button type="submit" className="button button--primary">
                Aplicar
              </button>
              <button
                type="button"
                className="button"
                disabled={activeFilters === 0}
                onClick={() => apply(EMPTY_RUN_FILTERS)}
              >
                Limpiar{activeFilters === 0 ? '' : ` (${activeFilters})`}
              </button>
            </div>
          </form>
        </div>
      </section>

      <ErrorBanner error={resource.error} onRetry={resource.reload} />

      <section className="panel">
        <header className="panel__header">
          <h2>{filters.flow === null ? 'Corridas' : `Corridas de ${flowLabel(filters.flow)}`}</h2>
        </header>

        {resource.loading && resource.data === null ? (
          <LoadingBlock />
        ) : rows.length === 0 ? (
          <EmptyState
            title="Ninguna corrida calza con los filtros."
            hint="Prueba ampliando el rango de fechas o quitando el filtro de estado. Los hilos sin flujo (logins, handshakes) no son corridas: están en Mensajes."
          />
        ) : (
          <div className="table__wrap">
            <table className="table table--stack">
              <thead>
                <tr>
                  <th scope="col">Inicio</th>
                  <th scope="col">Flujo</th>
                  <th scope="col">Estado</th>
                  <th scope="col">Mensajes</th>
                  <th scope="col">Duración</th>
                  <th scope="col">Comenzó con</th>
                  <th scope="col">Correlation id</th>
                  <th scope="col">Último error</th>
                </tr>
              </thead>
              <tbody>
                {rows.map((run) => (
                  <RunRow key={run.correlationId} run={run} />
                ))}
              </tbody>
            </table>
          </div>
        )}

        <Pagination
          page={page.page}
          pageSize={page.pageSize}
          total={resource.data?.total ?? 0}
          onPageChange={(nextPage) => apply(filters, { ...page, page: nextPage })}
          onPageSizeChange={(pageSize) => apply(filters, { page: 1, pageSize })}
        />
      </section>
    </div>
  );
}

function RunRow({ run }: { readonly run: FlowRunSummary }): ReactElement {
  const flowPath = `/flujo/${encodeURIComponent(run.correlationId)}`;
  const otherFlows = run.flows.filter((flow) => flow !== run.flow);
  const problems = run.failedCount + run.quarantinedCount;
  return (
    <tr>
      <td className="nowrap" data-label="Inicio">
        <Link className="table__open" to={flowPath}>
          {formatDateTime(run.startedAt)}
        </Link>
      </td>
      <td className="nowrap" data-label="Flujo">
        {run.flow === null ? (
          '—'
        ) : (
          <span className="flow-chip" title={flowLabel(run.flow)}>
            {run.flow}
          </span>
        )}
        {otherFlows.length === 0 ? null : (
          <span className="muted" title="La corrida también tiene pasos de estos flujos">
            {' '}
            + {otherFlows.join(', ')}
          </span>
        )}
      </td>
      <td className="table__pill">
        <RunStatusBadge status={run.status} />
      </td>
      <td className="nowrap" data-label="Mensajes">
        {run.messageCount}
        {problems === 0 ? null : (
          <span className="table__error">
            {' '}
            · {run.failedCount > 0 ? `${run.failedCount} con error` : null}
            {run.failedCount > 0 && run.quarantinedCount > 0 ? ', ' : null}
            {run.quarantinedCount > 0 ? `${run.quarantinedCount} en cuarentena` : null}
          </span>
        )}
        {run.runningCount === 0 ? null : (
          <span className="muted"> · {run.runningCount} en curso</span>
        )}
      </td>
      <td className="nowrap" data-label="Duración">
        {formatDuration(run.startedAt, run.endedAt)}
      </td>
      {/* El evento ya empieza por su sistema; el nombre largo queda en el título. */}
      <td className="mono" data-label="Comenzó con" title={systemLabel(run.firstSystem)}>
        <EventName value={run.firstEventType} />
      </td>
      <td data-label="Correlation id">
        <Link
          className="mono"
          to={`/traza/${encodeURIComponent(run.correlationId)}`}
          title={`${run.correlationId} — ver como lista`}
        >
          {shortId(run.correlationId)}
        </Link>
      </td>
      {run.lastError === null ? (
        <td className="muted table__placeholder" data-label="Último error">
          —
        </td>
      ) : (
        <td className="table__error table__cell--wide" data-label="Último error">
          <span className="truncate" title={run.lastError}>
            {run.lastError}
          </span>
        </td>
      )}
    </tr>
  );
}
